import { useEffect, useState } from "react";
import { FileSignature, Eye, Copy } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { listResource } from "@/lib/api";
import { toast } from "sonner";

export default function DocumentosLegales() {
  const [plantillas, setPlantillas] = useState([]);
  const [sel, setSel] = useState(null);
  const [vars, setVars] = useState({});
  const [preview, setPreview] = useState("");

  useEffect(() => { listResource("plantillas").then(setPlantillas); }, []);

  const open = (p) => {
    const init = {};
    (p.variables || []).forEach((v) => (init[v] = ""));
    setVars(init);
    setSel(p);
    setPreview("");
  };

  const substitute = () => {
    let txt = sel.contenido || "";
    Object.entries(vars).forEach(([k, v]) => {
      txt = txt.replaceAll(`{${k}}`, v || `{${k}}`);
    });
    setPreview(txt);
  };

  return (
    <div>
      <PageHeader title="Documentos legales" subtitle="Generador de documentos con plantillas y variables dinámicas" />
      <div className="p-6 md:p-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {plantillas.map((p) => (
            <button key={p.id} data-testid={`plantilla-${p.id}`} onClick={() => open(p)}
              className="rounded-lg border border-slate-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent hover:shadow-md">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10"><FileSignature className="h-5 w-5 text-primary" /></div>
              <p className="mt-3 font-heading text-sm font-semibold text-slate-800">{p.nombre}</p>
              <p className="text-xs text-slate-400">{p.categoria} · {(p.variables || []).length} variables</p>
            </button>
          ))}
        </div>
      </div>

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{sel.nombre}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Variables dinámicas</p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {(sel.variables || []).map((v) => (
                      <div key={v}>
                        <Label className="mb-1 block text-[11px] text-slate-500">{`{${v}}`}</Label>
                        <Input data-testid={`var-${v}`} value={vars[v] || ""} onChange={(e) => setVars((p) => ({ ...p, [v]: e.target.value }))} />
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button data-testid="preview-btn" variant="outline" size="sm" onClick={substitute}><Eye className="mr-1.5 h-4 w-4" /> Vista previa</Button>
                  </div>
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary">Documento</p>
                    {preview && <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(preview); toast.success("Copiado"); }}><Copy className="mr-1.5 h-4 w-4" /> Copiar</Button>}
                  </div>
                  <pre className="min-h-[260px] whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-4 font-sans text-sm leading-relaxed text-slate-700">
                    {preview || "Captura las variables y presiona “Vista previa” para generar el documento."}
                  </pre>
                  {preview && <p className="mt-2 text-[11px] text-amber-600">Documento de evidencia / borrador. Sujeto a validación humana y políticas internas.</p>}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
